import * as React from "react";
import { AdminPreferences, KendraConst, SearchInterface } from "@amzn/ask-legal-domain";
import { Badge, Button } from "@amzn/awsui-components-react";
import { AuthorFilterV2 } from "../search-configs/AuthorFilterV2";
import { FileTypeFilterV2 } from "../search-configs/FileTypeFilterV2";
import { LegalOnlyFilterV2 } from "../search-configs/LegalOnlyFilterV2";
import { SortingV2 } from "../search-configs/SortingV2";
import { LastUpdatedDateFilterV2 } from "../search-configs/LastUpdatedDateFilterV2";
import { PaginationSettingV2 } from "../search-configs/PaginationSettingV2";
import { PageLibraryFilterV2 } from "../search-configs/PageLibraryFilterV2";
import { InstanceFilterV2 } from "../search-configs/InstanceFilterV2";
import { getStringFilterValues, hasAttributeFilter, isSameValueSet } from "../search-configs/search-filter-utils-v2";
import { useCrossInstanceSearch } from "../../../hooks/use-cross-instance-search";
import { UIModel } from "../../../model/ui-model";
import "../../../styles/component/search/search-filters-v2.scss";

const PANEL_ID = "pf-search-filters-panel";
const ATTRIBUTES = KendraConst.ContainerIngestionKendraAttributes;

interface SmartSearchFilterViewV2Props {
    preferences: AdminPreferences.SearchPreferences;
    queryInputState: UIModel.State<SearchInterface.SearchQueryInput>;
    authors: string[];
    pageLibraries: string[];
    availableInstances?: string[];
    hasLegalOnlyAccess: boolean;
    isSearchRunning?: boolean;
    // Shows InstanceFilter for global search
    isGlobalSearch?: boolean;
    /** File types the page applies by default; only a different selection counts as an active filter */
    defaultFileTypes: string[];
    /** Resets the filters to the page's starting query (sort and page size are kept) */
    onClearFilters: () => void;
}

interface VisibleFilters {
    instance: boolean;
    authors: boolean;
    fileType: boolean;
    pageLibrary: boolean;
    lastUpdated: boolean;
    legalOnly: boolean;
}

/**
 * Number of shown filter controls that narrow the results (sort and page size are not counted),
 * so the badge only counts what the user can see and change.
 */
const countActiveFilters = (
    query: SearchInterface.SearchQueryInput,
    defaultFileTypes: string[],
    visible: VisibleFilters
): number => [
    visible.instance && hasAttributeFilter(query, ATTRIBUTES.Default.InstanceId),
    visible.authors && hasAttributeFilter(query, ATTRIBUTES.Default.Authors),
    visible.fileType && !isSameValueSet(getStringFilterValues(query, ATTRIBUTES.Custom.ContentType), defaultFileTypes),
    visible.pageLibrary && hasAttributeFilter(query, ATTRIBUTES.Custom.PageLibraryId),
    visible.lastUpdated && hasAttributeFilter(query, ATTRIBUTES.Default.LastUpdatedDate),
    visible.legalOnly && query.showStrictlyLegalOnly !== undefined
].filter(Boolean).length;

/**
 * Search filters as a "Filters" button with an active-filter badge that opens a wrapping row of
 * dropdowns above the results. The row stays mounted while closed.
 */
export const SmartSearchFilterViewV2: React.FC<SmartSearchFilterViewV2Props> = ({
    preferences,
    queryInputState,
    authors,
    pageLibraries,
    availableInstances,
    hasLegalOnlyAccess,
    isSearchRunning,
    isGlobalSearch = false,
    defaultFileTypes,
    onClearFilters
}) => {
    const isCrossInstanceSearchEnabled = useCrossInstanceSearch();
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const visible: VisibleFilters = {
        // Teams filter when instances exist and (global search or cross-instance enabled)
        instance: !!availableInstances?.length && (isGlobalSearch || isCrossInstanceSearchEnabled),
        authors: !!preferences.filterPrefs.showAuthors,
        fileType: preferences.filterPrefs.showFileType.length > 0,
        pageLibrary: !!preferences.filterPrefs.showPageLibrary,
        lastUpdated: !!preferences.filterPrefs.showLastUpdated,
        legalOnly: !!preferences.filterPrefs.showPermissionGroups && hasLegalOnlyAccess
    };
    const activeCount = countActiveFilters(queryInputState.value, defaultFileTypes, visible);

    return (
        <div className="pf-search-filters">
            <div className="pf-search-filters-toolbar">
                <Button
                    iconName="filter"
                    ariaExpanded={isOpen}
                    ariaControls={PANEL_ID}
                    ariaLabel={activeCount > 0 ? `Filters, ${activeCount} active` : "Filters"}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    Filters
                </Button>
                {activeCount > 0 && <Badge color="blue">{activeCount}</Badge>}
                {activeCount > 0 && (
                    <Button variant="inline-link" disabled={isSearchRunning} onClick={onClearFilters}>
                        Clear filters
                    </Button>
                )}
            </div>
            <div
                id={PANEL_ID}
                role="group"
                aria-label="Search filters"
                className={`pf-search-filters-panel${isOpen ? "" : " pf-search-filters-panel--closed"}`}
            >
                <div className="pf-search-filter">
                    <SortingV2
                        queryInputState={queryInputState}
                        preferences={preferences.sortPrefs}
                        disabled={isSearchRunning}
                    />
                </div>
                {visible.instance && (
                    <div className="pf-search-filter">
                        <InstanceFilterV2
                            instanceIds={availableInstances}
                            queryInputState={queryInputState}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
                {visible.authors && (
                    <div className="pf-search-filter">
                        <AuthorFilterV2
                            authors={authors}
                            queryInputState={queryInputState}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
                {visible.fileType && (
                    <div className="pf-search-filter">
                        <FileTypeFilterV2
                            queryInputState={queryInputState}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
                {visible.pageLibrary && (
                    <div className="pf-search-filter">
                        <PageLibraryFilterV2
                            pageLibraryIds={pageLibraries}
                            queryInputState={queryInputState}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
                {visible.lastUpdated && (
                    <div className="pf-search-filter">
                        <LastUpdatedDateFilterV2
                            queryInputState={queryInputState}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
                {visible.legalOnly && (
                    <div className="pf-search-filter">
                        <LegalOnlyFilterV2
                            queryInputState={queryInputState}
                            hasLegalOnlyAccess={hasLegalOnlyAccess}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
                {preferences.paginationPrefs.length > 0 && (
                    <div className="pf-search-filter">
                        <PaginationSettingV2
                            queryInputState={queryInputState}
                            prefs={preferences.paginationPrefs}
                            disabled={isSearchRunning}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
